import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { imagesFromClipboard, prepareScreenshot, MAX_SCREENSHOTS } from '../../util/screenshotAttachment';

// Only the most recently mounted enabled field reacts to a paste, so a dialog
// opened over a form does not attach the same screenshot twice.
const pasteStack = [];

// Screenshot attachments for a form: paste with Ctrl+V anywhere on the page, or
// pick a file. Images are scaled down in the browser; the parent holds the list
// and uploads it once the record exists.
export default function ScreenshotAttachments({
  value,
  onChange,
  max = MAX_SCREENSHOTS,
  enabled = true,
  hint = 'Screenshot mit Strg+V einfuegen',
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const addFiles = useCallback(
    async files => {
      const images = files.filter(file => String(file.type).startsWith('image/'));
      if (images.length === 0) return;
      const free = max - valueRef.current.length;
      if (free <= 0) {
        toast.error(`Mehr als ${max} Screenshots gehen nicht.`);
        return;
      }
      setBusy(true);
      try {
        const prepared = await Promise.all(images.slice(0, free).map(prepareScreenshot));
        onChangeRef.current([
          ...valueRef.current,
          ...prepared.map((item, index) => ({ ...item, id: `${Date.now()}-${index}` })),
        ]);
        if (images.length > free) {
          toast.warn(`Nur ${free} von ${images.length} Bildern uebernommen (max. ${max}).`);
        }
      } catch (err) {
        toast.error('Screenshot konnte nicht gelesen werden.');
      } finally {
        setBusy(false);
      }
    },
    [max]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    const token = Symbol('screenshot-paste');
    pasteStack.push(token);

    const handlePaste = event => {
      if (pasteStack[pasteStack.length - 1] !== token) return;
      const files = imagesFromClipboard(event);
      if (files.length === 0) return;
      // Only now: a paste without an image stays a normal text paste.
      event.preventDefault();
      addFiles(files).catch(() => {});
    };

    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
      const index = pasteStack.indexOf(token);
      if (index >= 0) pasteStack.splice(index, 1);
    };
  }, [enabled, addFiles]);

  const remove = id => onChange(value.filter(item => item.id !== id));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Button
          size='small'
          variant='outlined'
          startIcon={<AddPhotoAlternateOutlinedIcon />}
          disabled={busy || value.length >= max}
          onClick={() => inputRef.current?.click()}
        >
          Screenshot anhaengen
        </Button>
        <Typography variant='caption' color='text.secondary'>
          {value.length >= max ? `Maximal ${max} Screenshots` : hint}
        </Typography>
        <input
          ref={inputRef}
          type='file'
          accept='image/*'
          multiple
          hidden
          onChange={event => {
            addFiles(Array.from(event.target.files || [])).catch(() => {});
            event.target.value = '';
          }}
        />
      </Box>

      {value.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {value.map(item => (
            <Box key={item.id} sx={{ position: 'relative' }}>
              <Box
                component='img'
                src={item.previewUrl}
                alt={item.fileName}
                sx={{
                  width: 112,
                  height: 80,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                }}
              />
              <IconButton
                size='small'
                aria-label={`${item.fileName} entfernen`}
                onClick={() => remove(item.id)}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                <CloseIcon fontSize='inherit' />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
